/* The interactive application of a lesson: running, and downloadable.

   A teacher decides from the artefact, not from a description of it, so the
   application runs here on the page. And the way it will actually be used is as
   a file — opened from a school disk, off a flash drive, from a folder on a
   laptop with no network — so the button beside it hands over that same file,
   made in the browser out of the text that was fetched. One fetch, both uses.

   **The frame is sandboxed and has no allow-same-origin.** The file is served
   from the API's origin, which is where the administrator's session cookie
   lives; a frame that could reach that origin could reach the API as whoever is
   signed in. The response says the same thing in a header — see
   api/src/content.ts — so the two do not depend on each other being right.

   The consequence is worth knowing: inside the frame the document has an opaque
   origin, so localStorage and friends are unavailable. That is a preview, not a
   cage — the downloaded file has none of that restriction, which is the other
   reason the button is there.

   OWNED BY ITEM 5 OF PLAN.md. */

import { useEffect, useState } from "react";
import { loadApp } from "../lib/content";
import { useI18n } from "../lib/i18n";
import { Head, Notes, SANDBOX } from "./shared";
import { ModelPrompt } from "./prompts";
import type { AppBlock, DeviceKind } from "../lib/types";

/* The same frame as a block of a page.

   A general page has no kit to hang an application on, and the two pages this
   was written for — HTML slides, and the interactive elements that go into them
   — would otherwise be prose about things the reader cannot see working. The
   prompt sits inside the block rather than beside it because the pair is the
   point: this is what it does, and this is what makes you one. */
export function AppExample({ b }: { b: AppBlock }) {
  return (
    <>
      <Head b={b} />
      <AppFrame app={b.app} height={b.height} device={b.device} />
      {b.prompt ? <ModelPrompt prompt={b.prompt} /> : null}
      <Notes b={b} />
    </>
  );
}

/* Left out, the device is the Mac at the front of the room. See DeviceKind in
   lib/types.ts for why the frame is never bare. */
export function AppFrame({ app, height, device = "mac" }:
                         { app: string; height?: number; device?: DeviceKind }) {
  const { ui } = useI18n();
  const [code, setCode] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setCode(null);
    setFailed(false);
    loadApp(app).then(
      (text) => { if (live) setCode(text); },
      () => { if (live) setFailed(true); },
    );
    return () => { live = false; };
  }, [app]);

  function download() {
    if (code === null) return;
    const url = URL.createObjectURL(new Blob([code], { type: "text/html;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${app}.html`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 8000);
  }

  /* On paper a running frame is a grey rectangle. The bar under it is not:
     it names the file, which is what somebody reading the sheet needs. */
  const run = (
    <div className="appframe-run noprint" style={{ height: `${height ?? 460}px` }}>
      {failed ? <p className="appframe-failed">{ui("app.failed")}</p> : null}
      {/* The frame is not drawn until the file is in hand, and it is keyed on
          the file. Both matter: a sandboxed frame that has already committed
          an empty document does not reload when srcdoc is changed underneath
          it, so an iframe rendered first and filled in afterwards stays blank
          — which is exactly what happened here until this was measured. */}
      {code !== null ? (
        <iframe key={app} title={ui("app.title")} sandbox={SANDBOX} srcDoc={code} />
      ) : null}
    </div>
  );

  return (
    <div className="appframe">
      {/* The screen the application is shown on. A file that will be opened on
          a school machine at the projector, or on a telephone, is not the same
          thing as a panel of the portal, and a bare rectangle says it is. The
          shell is drawn in CSS around the live frame — it has to be, the frame
          inside it is running. */}
      <div className={`device device-${device} noprint`}>
        <div className="device-shell">{run}</div>
        {device === "mac" ? (
          <><i className="device-neck" /><i className="device-foot" /></>
        ) : null}
        {device === "macbook" ? <i className="device-base" /> : null}
      </div>
      <div className="appframe-bar">
        <span className="appframe-name">{ui("lesson.appFile")} <code>{app}.html</code></span>
        <button type="button" className="appframe-get noprint"
                onClick={download} disabled={code === null}>
          {ui("app.download")}
        </button>
      </div>
    </div>
  );
}
